import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  FluentProvider,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import {
  APIProvider,
  AdvancedMarker,
  Map,
  Pin,
} from '@vis.gl/react-google-maps';
import type { MapMouseEvent } from '@vis.gl/react-google-maps';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useActiveTheme } from '../theme/ThemeContext';

export interface ILocation {
  latitude: number;
  longitude: number;
}

const defaultLocation: ILocation = {
  latitude: -33.7613856,
  longitude: 150.9889242,
};

export class LocationHelper {
  static locationToString = (aLocation: ILocation): string => {
    return `lat: ${aLocation.latitude.toPrecision(
      9
    )}, lng: ${aLocation.longitude.toPrecision(9)}`;
  };
  static fromString = (locationStr: string): ILocation => {
    try {
      const parts = locationStr.split(',');
      const lat = Number.parseFloat(parts[0].replace('lat: ', ''));
      const lng = Number.parseFloat(parts[1].replace(' lng: ', ''));
      return { latitude: lat, longitude: lng };
    } catch {
      return defaultLocation;
    }
  };
}

export interface IMapPickDialogParams {
  isModalOpen: boolean;
  hideModal: () => void;
  onMapClick: (newLocation: ILocation) => void;
  location?: ILocation;
}

const useStyles = makeStyles({
  surface: {
    maxWidth: '720px',
    width: '100%',
  },
  map: {
    height: '420px',
    width: '100%',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalM,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexShrink: 0,
  },
});

const MapPickDialog = (params: IMapPickDialogParams) => {
  const theme = useActiveTheme();
  const { t } = useTranslation();
  const styles = useStyles();
  const apiKey = import.meta.env.VITE_GOOGLE_MAP_APIKEY as string | undefined;
  const [newLocation, setNewLocation] = useState<ILocation>(
    params.location ?? defaultLocation
  );
  const center = {
    lat: params.location?.latitude ?? newLocation.latitude,
    lng: params.location?.longitude ?? newLocation.longitude,
  };

  return (
    <FluentProvider theme={theme}>
      <Dialog
        open={params.isModalOpen}
        onOpenChange={(_, data) => {
          if (!data.open) params.hideModal();
        }}
      >
        <DialogSurface className={styles.surface}>
          <DialogBody>
            <DialogTitle
              action={
                <Button
                  appearance="subtle"
                  aria-label={t('close', 'Close')}
                  icon={<Dismiss24Regular />}
                  onClick={params.hideModal}
                />
              }
            >
              {t('pickTheLocation', 'Pick the location')}
            </DialogTitle>
            <DialogContent>
              {!apiKey ? (
                t(
                  'googleMapsApiKeyMissing',
                  'Google Maps API key is not configured.'
                )
              ) : (
                <div className={styles.map}>
                  <APIProvider apiKey={apiKey} libraries={['marker']}>
                    <Map
                      defaultCenter={center}
                      defaultZoom={14}
                      mapId="coordinate-pick-map"
                      style={{ width: '100%', height: '100%' }}
                      gestureHandling="greedy"
                      onClick={(event: MapMouseEvent) => {
                        setNewLocation({
                          latitude:
                            event.detail.latLng?.lat ??
                            defaultLocation.latitude,
                          longitude:
                            event.detail.latLng?.lng ??
                            defaultLocation.longitude,
                        });
                      }}
                    >
                      <AdvancedMarker
                        position={{
                          lat: newLocation.latitude,
                          lng: newLocation.longitude,
                        }}
                      >
                        <Pin
                          background={theme.colorBrandBackground}
                          glyphColor={theme.colorNeutralForegroundOnBrand}
                          borderColor={theme.colorBrandStroke1}
                        />
                      </AdvancedMarker>
                    </Map>
                  </APIProvider>
                </div>
              )}
              <div className={styles.footer}>
                <Text size={200}>
                  {LocationHelper.locationToString(newLocation)}
                </Text>
                <div className={styles.actions}>
                  <Button appearance="secondary" onClick={params.hideModal}>
                    {t('cancel', 'Cancel')}
                  </Button>
                  <Button
                    appearance="primary"
                    onClick={() => params.onMapClick(newLocation)}
                  >
                    {t('ok', 'Ok')}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </FluentProvider>
  );
};

export default MapPickDialog;
